import React from "react";
import { generateClassName, generateStyle } from "../../../hooks/useAttributes";
import { AppRoutes } from "../../../routes/AppRouter";
import { IAppPath } from "../../../routes/AppRoutes";
import { useUsage } from "../../../state/api/ai/useUsage";
import { useSession } from "../../../state/api/session/useSession";
import Icon from "../icons/Icon";
import Logo from "../logo/Logo";
import LogOutButton from "../session/LogOutButton";
import "./SideNav.css";
import SideNavLink from "./SideNavLink";

export interface IElementPosition {
  left: number,
  top: number,
  width: number,
  height: number
}

export interface ISideNavTooltip {
  position: IElementPosition,
  title: string,
  tooltip: string
}

export const SideNavIconSize = 24;

export default function SideNav() {

  const { session } = useSession();

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const [highlightPos, setHighlightPos] = React.useState<IElementPosition>({ left: 0, top: 0, width: 0, height: 0 });
  const [usedPos, setUsedPos] = React.useState<IElementPosition>({ left: 0, top: 0, width: 0, height: 0 })

  const [tooltip, setTooltip] = React.useState<ISideNavTooltip>({ position: { left: 0, top: 0, width: 0, height: 0 }, title: "", tooltip: "" });

  const hideTimer = React.useRef<any>();
  const showTimer = React.useRef<any>();

  const [tooltipWillShow, setTooltipWillShow] = React.useState<boolean>(false);
  const [tooltipWillDisappear, setTooltipWillDisappear] = React.useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = React.useState<boolean>(false);

  const navRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const e = navRef.current;

    if (!e) {
      return;
    }

    const navBarRect = e.getBoundingClientRect();

    if (!navBarRect) {
      setUsedPos(highlightPos);
      return;
    }

    const scrollOffsetTop = highlightPos.top - navBarRect.top + e.scrollTop;
    const hasToScroll = e.scrollHeight > (navBarRect.width + 1);

    setUsedPos({
      left: expanded ? highlightPos.left : 0,
      top: scrollOffsetTop,
      width: expanded ? highlightPos.width : 5,
      height: highlightPos.height
    });

    if (hasToScroll) navRef.current.scrollTo({ top: scrollOffsetTop, behavior: "smooth" });
  }, [expanded, highlightPos]);

  const showTooltip = (t: ISideNavTooltip | null) => {
    clearTimeout(hideTimer.current);

    if (!t) {
      setTooltipWillShow(false);
      setTooltipWillDisappear(true);
      clearTimeout(showTimer.current);
      hideTimer.current = setTimeout(() => setTooltipVisible(false), 400);
      return;
    }

    setTooltip(t);

    setTooltipWillDisappear(false);

    hideTimer.current = setTimeout(() => showTooltip(null), 2000);

    if (tooltipVisible) {
      setTooltipWillShow(false);
      setTooltipVisible(true);
      return;
    }

    setTooltipWillShow(true);
    const y = setTimeout(() => setTooltipVisible(true), 100);

    showTimer.current = y;
  }


  const getSidebarLink = (path: IAppPath, display: boolean = true) => (
    <SideNavLink
      path={path}
      visible={display}
      toggleTooltip={showTooltip}
      closeSidebar={() => setExpanded(false)}
      sideBarIsExpanded={expanded}
      setHighlightPosition={setHighlightPos}
    />
  )

  const sideNavClass = generateClassName("top-0 start-0 d-flex flex-column align-items-start pb-3 h-100 sidenav", {
    value: expanded,
    base: "sidenav-",
    standard: "collapsed",
    onTrue: "expanded ps-3"
  });

  const sideNavHighlightStyle = generateStyle({
    name: "height",
    value: usedPos.height,
    unit: "px"
  }, {
    name: "width",
    value: usedPos.width,
    unit: "px"
  }, {
    name: "transform",
    value: expanded ? `translateX(${usedPos.left}px)` : 'translateX(100%)',
    unit: "px"
  }, {
    name: "top",
    value: usedPos.top,
    unit: "px"
  }, {
    name: "backgroundColor",
    value: "primaryAccent",
  });

  const sideNavHighlightClass = generateClassName("side-nav-highlight position-absolute", {
    value: expanded,
    onTrue: "side-nav-highlight-expanded"
  })

  const sideNavTooltipStyle = generateStyle({
    name: "top",
    value: tooltip.position.top,
    unit: "px"
  });

  const sideNavTooltipClass = generateClassName("d-flex flex-column gap-1 sidenav-tooltip ps-3 pe-3 p-2 position-absolute", {
    value: tooltipWillShow,
    onTrue: "sidenav-tooltip-will-show"
  }, {
    value: tooltipWillDisappear,
    onTrue: "sidenav-tooltip-will-hide"
  })

  return (
    <div className={sideNavClass} ref={navRef} >
      <div className={sideNavHighlightClass} style={sideNavHighlightStyle}></div>
      {
        (tooltipVisible && !expanded) && (
          <div className={sideNavTooltipClass} style={sideNavTooltipStyle}>
            <strong className="font-code text-nowrap">{tooltip.title}</strong>

            {tooltip.tooltip && (
              <span className="text-nowrap text-truncate">{tooltip.tooltip}</span>
            )
            }
          </div>
        )
      }
      <div className="d-flex pt-4 mb-3 align-items-center justify-content-center sidenav-top-container" >
        <div className="sidenav-toggle-container" >
          <Icon onClick={() => setExpanded(!expanded)} icon={expanded ? "x" : "list"} size={SideNavIconSize} />
        </div>
        <div className="sidenav-logo-container d-none">
          <Logo size={149} />
        </div>
      </div>
      <div className="h-100 w-100 d-flex flex-column align-items-center justify-content-between">
        <div className="d-flex flex-column gap-2 w-100" style={{ maxHeight: "50vh" }}>
          {getSidebarLink(AppRoutes.Products)}
          {getSidebarLink(AppRoutes.AI)}
          {getSidebarLink(AppRoutes.UsefulLinks)}
          {getSidebarLink(AppRoutes.Favorites)}
          {getSidebarLink(AppRoutes.AdminArea, session?.isAdmin)}
        </div>
        <div className="d-flex flex-column gap-2 w-100" >
          <Usage sidenavExpanded={expanded} />
          {getSidebarLink(AppRoutes.About)}
          <LogOutButton withText={expanded} />
        </div>
      </div>
    </div>
  )
}

interface UsageProps {
  sidenavExpanded: boolean
}


export function Usage(props: UsageProps) {
  const { sidenavExpanded } = props;
  const { usage } = useUsage();

  if (!usage) {
    return null;
  }

  const { outputUsed, inputUsed } = usage;

  const inputClamp = Math.min(Math.max(inputUsed, 0), 1);
  const outputClamp = Math.min(Math.max(outputUsed, 0), 1);

  const HALF_CIRCUMFERENCE = 47.124;

  const outputDash = outputClamp * HALF_CIRCUMFERENCE;
  const inputDash = inputClamp * HALF_CIRCUMFERENCE;

  const tooltipText = `Input: ${(inputClamp * 100).toFixed(0)}% | Output: ${(outputClamp * 100).toFixed(0)}%`;

  return (
    <div
      className="d-flex flex-row w-100 justify-content-start align-items-center sidenav-link usage"
      title={tooltipText}
    >
      <div style={{ width: "var(--sideNavWidth)", aspectRatio: '1 / 1', position: 'relative' }}>
        <svg viewBox="0 0 40 40" style={{ width: '100%', height: '100%' }}>
          <circle
            cx="20"
            cy="20"
            r="15"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="4"
          />

          <path
            d="M 20 5 A 15 15 0 0 1 20 35"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="4"
            strokeDasharray={`${outputDash} 999`}
          />

          <path
            d="M 20 5 A 15 15 0 0 0 20 35"
            fill="none"
            stroke="#ef4444"
            strokeWidth="4"
            strokeDasharray={`${inputDash} 999`}
          />
        </svg>
      </div>
      <div className={generateClassName("sidenav-link-text d-none flex-column overflow-hidden", {
        value: sidenavExpanded,
        onTrue: "sidenav-link-text-expanded"
      })}>
        <p className="text-uppercase sidenav-link-label m-0 p-0 fs-6 font-code fw-bold text-nowrap">Usage</p>
        <span className="sidenav-link-description font-s text-truncate m-0 p-0">{tooltipText}</span>
      </div>
    </div>
  );
}